import { Routes, Route, Navigate } from 'react-router-dom';

import RequireAuth from './lib/RequireAuth';
import { ConfirmProvider } from './lib/confirm';
import { ToastProvider } from './lib/toast';
import NotFound from './pages/NotFound';

// Phase 1
import SignInScreen from './pages/auth/SignInScreen';
import RegisterScreen from './pages/auth/RegisterScreen';
import AcceptInviteScreen from './pages/auth/AcceptInviteScreen';
import ResetPasswordScreen from './pages/auth/ResetPasswordScreen';
import CreateTeamScreen from './pages/workspace/CreateTeamScreen';

// Phase 2
import HubScreen from './pages/HubScreen';

// Phase 3a — workspace shell + tabs
import WorkspaceShell from './components/layout/WorkspaceShell';
import OverviewPanel from './pages/workspace/OverviewPanel';
import DocumentsPanel from './pages/workspace/DocumentsPanel';
import MembersPanel from './pages/workspace/MembersPanel';
import AutomationsPanel from './pages/workspace/AutomationsPanel';
import IntegrationsPanel from './pages/workspace/IntegrationsPanel';

// Phase 3b — Templates / Billing / Settings
import TemplatesPanel from './pages/workspace/TemplatesPanel';
import RolesPanel from './pages/workspace/RolesPanel';
import MyDocsPanel from './pages/workspace/MyDocsPanel';
// NOTE: BillingPanel removed Phase 3C — billing is now account-level
// via AccountScreen's BillingTab. /workspace/:id/billing redirects to /account.
import SettingsPanel from './pages/workspace/SettingsPanel';

// Phase 4 — Portal + Account
import PortalScreen from './pages/PortalScreen';
import AccountScreen from './pages/AccountScreen';

export default function App() {
  return (
    <ToastProvider>
    <ConfirmProvider>
    <Routes>
      {/* ── Root redirect ── */}
      <Route path="/" element={<Navigate to="/sign-in" replace />} />

      {/* ── Public auth routes (Phase 1) ── */}
      <Route path="/sign-in"        element={<SignInScreen />} />
      <Route path="/register"       element={<RegisterScreen />} />
      <Route path="/accept-invite"  element={<AcceptInviteScreen />} />
      <Route path="/reset-password" element={<ResetPasswordScreen />} />

      {/* ── Hub (Phase 2) ── */}
      <Route
        path="/hub"
        element={
          <RequireAuth>
            <HubScreen />
          </RequireAuth>
        }
      />

      {/* ── Create team (Phase 1) ── */}
      <Route
        path="/create-team"
        element={
          <RequireAuth>
            <CreateTeamScreen />
          </RequireAuth>
        }
      />

      {/* ── Workspace shell + nested tabs (Phase 3a) ── */}
      <Route
        path="/workspace/:teamId"
        element={
          <RequireAuth>
            <WorkspaceShell activeTab="overview" />
          </RequireAuth>
        }
      >
        <Route index element={<OverviewPanel />} />
      </Route>

      <Route
        path="/workspace/:teamId/documents"
        element={
          <RequireAuth>
            <WorkspaceShell activeTab="documents" />
          </RequireAuth>
        }
      >
        <Route index element={<DocumentsPanel />} />
      </Route>

      <Route
        path="/workspace/:teamId/members"
        element={
          <RequireAuth>
            <WorkspaceShell activeTab="members" />
          </RequireAuth>
        }
      >
        <Route index element={<MembersPanel />} />
      </Route>

      <Route
        path="/workspace/:teamId/templates"
        element={
          <RequireAuth>
            <WorkspaceShell activeTab="templates" />
          </RequireAuth>
        }
      >
        <Route index element={<TemplatesPanel />} />
      </Route>

      <Route
        path="/workspace/:teamId/roles"
        element={
          <RequireAuth>
            <WorkspaceShell activeTab="roles" />
          </RequireAuth>
        }
      >
        <Route index element={<RolesPanel />} />
      </Route>

      <Route
        path="/workspace/:teamId/my-docs"
        element={
          <RequireAuth>
            <WorkspaceShell activeTab="my-docs" />
          </RequireAuth>
        }
      >
        <Route index element={<MyDocsPanel />} />
      </Route>

      <Route
        path="/workspace/:teamId/automations"
        element={
          <RequireAuth>
            <WorkspaceShell activeTab="automations" />
          </RequireAuth>
        }
      >
        <Route index element={<AutomationsPanel />} />
      </Route>

      {/* Phase 3C: billing moved to account-level. Old URLs redirect. */}
      <Route
        path="/workspace/:teamId/billing"
        element={<Navigate to="/account?tab=billing" replace />}
      />

      <Route
        path="/workspace/:teamId/integrations"
        element={
          <RequireAuth>
            <WorkspaceShell activeTab="integrations" />
          </RequireAuth>
        }
      >
        <Route index element={<IntegrationsPanel />} />
      </Route>

      <Route
        path="/workspace/:teamId/settings"
        element={
          <RequireAuth>
            <WorkspaceShell activeTab="settings" />
          </RequireAuth>
        }
      >
        <Route index element={<SettingsPanel />} />
      </Route>

      {/* ── Legacy flat routes — redirect to nearest equivalent or /hub ── */}
      <Route path="/workspace"   element={<Navigate to="/hub" replace />} />
      <Route path="/documents"   element={<Navigate to="/hub" replace />} />
      <Route path="/members"     element={<Navigate to="/hub" replace />} />
      <Route path="/templates"   element={<Navigate to="/hub" replace />} />
      <Route path="/automations" element={<Navigate to="/hub" replace />} />
      <Route path="/billing"     element={<Navigate to="/hub" replace />} />
      <Route path="/integrations" element={<Navigate to="/hub" replace />} />
      <Route path="/settings"    element={<Navigate to="/hub" replace />} />

      {/* ── Client portal (Phase 4) ── */}
      <Route
        path="/portal"
        element={
          <RequireAuth>
            <PortalScreen />
          </RequireAuth>
        }
      />

      {/* ── Account (Phase 4) ── */}
      <Route
        path="/account"
        element={
          <RequireAuth>
            <AccountScreen />
          </RequireAuth>
        }
      />

      {/* ── 404 ── */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </ConfirmProvider>
    </ToastProvider>
  );
}
